import { API_CONFIG } from "../config/api";
import { COMMON_CONFIG } from "../config/constants";
import { ApiResponse } from "../types";
import ErrorHandler, { ApiError, ApiErrorType } from "../utils/errorHandler";
import TokenManager from "../utils/tokenManager";

// 请求拦截器
const requestInterceptor = (config: RequestInit): RequestInit => {
  // 添加认证 token
  const accessToken = TokenManager.getAccessToken();
  if (accessToken) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${accessToken}`,
    };
  }

  return config;
};

// 响应拦截器
const responseInterceptor = async (response: Response): Promise<any> => {
  const data = await response.json();

  // 检查 HTTP 状态码
  if (!response.ok) {
    // 根据状态码创建相应的错误
    let apiError: ApiError;

    if (response.status === 401) {
      // Token 过期，清除 token 并提示重新登录
      TokenManager.clearToken();
      apiError = ErrorHandler.createAuthError(
        data.msg || "登录已过期，请重新登录"
      );
    } else if (response.status === 403) {
      apiError = new ApiError(
        data.msg || "没有权限",
        ApiErrorType.AUTH_ERROR,
        response.status
      );
    } else if (response.status === 422) {
      apiError = ErrorHandler.createValidationError(data.msg || "数据验证失败");
    } else if (response.status >= 500) {
      apiError = ErrorHandler.createServerError(data.msg || "服务器错误");
    } else {
      apiError = new ApiError(
        data.msg || `HTTP ${response.status}: ${response.statusText}`,
        ApiErrorType.UNKNOWN_ERROR,
        response.status
      );
    }

    throw apiError;
  }

  // 适配 OpenAPI 响应格式
  const apiResponse: ApiResponse = {
    code: data.code,
    msg: data.msg,
    data: data.data,
  };

  // 检查业务状态码
  if (data.code !== 0 && data.code !== undefined) {
    // 处理4001无权限错误码
    if (data.code === 4001) {
      // 跳转到无权限页面
      setTimeout(() => {
        window.location.href = '/unauthorized';
      }, 100);
      
      const apiError = new ApiError(
        data.msg || "没有权限访问",
        ApiErrorType.AUTH_ERROR,
        response.status
      );
      throw apiError;
    }

    // 根据业务状态码创建相应的错误
    let apiError: ApiError;

    if (data.error === "RATE_LIMIT_EXCEEDED") {
      // 频率限制错误
      apiError = new ApiError(
        data.msg || "操作过于频繁，请稍后再试",
        ApiErrorType.VALIDATION_ERROR,
        response.status
      );
    } else if (data.error === "VALIDATION_ERROR") {
      // 参数错误
      apiError = ErrorHandler.createValidationError(data.msg || "参数错误");
    } else if (data.error === "RESOURCE_NOT_FOUND") {
      // 资源不存在
      apiError = new ApiError(
        data.msg || "资源不存在",
        ApiErrorType.UNKNOWN_ERROR,
        response.status
      );
    } else {
      // 其他业务错误
      apiError = new ApiError(
        data.msg || "操作失败",
        ApiErrorType.UNKNOWN_ERROR,
        response.status
      );
    }

    throw apiError;
  }

  return apiResponse;
};

// 通用请求方法
export const request = async <T = any>(
  url: string,
  options: RequestInit = {},
  retryCount: number = 0
): Promise<ApiResponse<T>> => {
  try {
    const config = requestInterceptor({
      ...API_CONFIG.REQUEST_CONFIG,
      ...options,
    });

    // 处理 URL 添加 version 参数
    let finalUrl = url;
    if (options.method === "GET" || options.method === "DELETE") {
      // 对于 GET 和 DELETE 请求，将 version 添加到 URL 查询参数
      const separator = finalUrl.includes("?") ? "&" : "?";
      finalUrl += `${separator}version=${COMMON_CONFIG.VERSION}`;
    }

    // 处理请求体添加 version 参数
    let finalBody = options.body;
    if (
      (options.method === "POST" ||
        options.method === "PUT" ||
        options.method === "PATCH") &&
      options.body
    ) {
      try {
        // 尝试解析 JSON 并添加 version 参数
        const bodyObj = JSON.parse(options.body as string);
        const bodyWithVersion = { ...bodyObj, version: COMMON_CONFIG.VERSION };
        finalBody = JSON.stringify(bodyWithVersion);
      } catch (e) {
        // 如果不是有效的 JSON，保持原样
        console.warn("无法将 version 参数添加到非 JSON 格式的请求体");
      }
    }

    const response = await fetch(finalUrl, {
      ...config,
      body: finalBody,
    });

    const data = await responseInterceptor(response);

    return data;
  } catch (error) {
    // 如果是认证错误，清除 token 并跳转到登录页
    if (error instanceof ApiError && error.type === ApiErrorType.AUTH_ERROR) {
      TokenManager.clearToken();
      // 延迟跳转，避免立即刷新
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }

    // 使用错误处理工具处理错误
    throw ErrorHandler.handleApiError(error, false);
  }
};

// HTTP 方法封装
export const http = {
  get: <T = any>(
    url: string,
    params?: Record<string, any>
  ): Promise<ApiResponse<T>> => {
    let finalUrl = url;

    // 添加查询参数
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        const separator = finalUrl.includes("?") ? "&" : "?";
        finalUrl += `${separator}${queryString}`;
      }
    }

    return request<T>(finalUrl, {
      method: "GET",
    });
  },

  post: <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    return request<T>(url, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  put: <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    return request<T>(url, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  patch: <T = any>(url: string, data?: any): Promise<ApiResponse<T>> => {
    return request<T>(url, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  delete: <T = any>(url: string): Promise<ApiResponse<T>> => {
    return request<T>(url, {
      method: "DELETE",
    });
  },
};

// 构建完整 URL 的辅助函数
export const buildUrl = (
  endpoint: string,
  params?: Record<string, string>
): string => {
  let url = API_CONFIG.getBaseURL() + endpoint;

  if (params) {
    Object.keys(params).forEach((key) => {
      url = url.replace(`:${key}`, params[key]);
    });
  }

  return url;
};
